import React, { useState, useEffect } from "react";
import "./App.css";
import Navbar, { type HeaderLogoConfig } from "./components/Navbar";
import AnimatedBackground from "./components/AnimatedBackground";
import ThemeSwitcher from "./components/ThemeSwitcher";
import Footer, { type FooterConfig } from "./components/Footer";
import AuthDialog from "./components/AuthDialog";
import Profile from "./components/Profile";
import Loader from "./components/Loader";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import {
  NotificationProvider,
  useNotification,
} from "./contexts/NotificationContext";
import { applyThemeToDocument, setCurrentTheme, colorThemes } from "./config/colors";
import KeyDifferences, { type FeatureItem } from "./components/KeyDifferences";
import PricingSection from "./components/PricingSection";
import CasinoLandingCard from "./components/CasinoLandingCard";
import HowItWorksPlayer from "./components/HowItWorksPlayer";
import PickYourFocus from "./components/PickYourFocus";
import Features, { type FeaturesConfig } from "./components/Features";
import AudienceCards, { type AudienceCardsConfig } from "./components/AudienceCards";
import FAQ, { type FAQConfig } from "./components/FAQ";
import DemoRequestForm from "./components/DemoRequestForm";
import { getUserFullName } from "./utils/auth";
import { BusinessProvider, useBusinessContext } from "./contexts/BusinessContext";
import { PricingProvider, PricingContent, PricingPlan, defaultPricingContent } from "./contexts/PricingContext";
import { OPEN_ORDERS_DIALOG_EVENT } from "./utils/ordersEvents";
import { CountrySelectionDialog } from "./components/CountrySelectionDialog";
import { CountryDetailsDialog } from "./components/CountryDetailsDialog";
import { PricingPlansDialog } from "./components/PricingPlansDialog";
import { BusinessSetupDialog } from "./components/BusinessSetupDialog";
import { WebsiteStudioDialog } from "./components/WebsiteStudioDialog";
import { OrderDetailsDialog } from "./components/OrderDetailsDialog";
import { PaymentDialog } from "./components/PaymentDialog";
import UserOrdersSection from "./components/UserOrdersSection";

type HeroContent = {
  title: string;
  tagline: string;
  description: string;
  primaryCtaLabel: string;
  secondaryCtaLabel: string;
};

type PackageCard = {
  name: string;
  price: string;
  buttonText?: string;
};

type HowItWorksConfig = {
  title: string;
  steps: Array<{
    id: number;
    title: string;
    emoji: string;
    elements: Array<{
      type: "globe" | "cards" | "input" | "crypto" | "dashboard" | "launch";
      content?: string;
      packages?: PackageCard[];
    }>;
  }>;
  colors: {
    background: string;
    backgroundGradient: string;
    primary: string;
    secondary: string;
    accent: string;
    textGlow: string;
  };
  timing: {
    stepDuration: number;
    transitionDuration: number;
  };
  text: {
    titleSize: string;
    stepSize: string;
  };
};

// Default config as fallback
const defaultConfig: HowItWorksConfig = {
  title: "How it works",
  steps: [
    {
      id: 1,
      title: "Step 1: Choose Your Country 🌍",
      emoji: "🌍",
      elements: [{ type: "globe" as const }],
    },
    {
      id: 2,
      title: "Step 2: Select Your Package 💼",
      emoji: "💼",
      elements: [
        {
          type: "cards" as const,
          packages: [
            { name: "Starter", price: "$99", buttonText: "Choose Plan" },
            { name: "Pro", price: "$299", buttonText: "Choose Plan" },
            { name: "Enterprise", price: "$999", buttonText: "Choose Plan" },
          ],
        },
      ],
    },
    {
      id: 3,
      title: "Step 3: Give Your Casino a Name 🎰",
      emoji: "🎰",
      elements: [{ type: "input" as const, content: "LuckyDice.io" }],
    },
    {
      id: 4,
      title: "Step 4: Pay Securely with Crypto 💳",
      emoji: "💳",
      elements: [{ type: "crypto" as const }],
    },
    {
      id: 5,
      title: "Step 5: Customize Everything — Layout, Games, Themes & More! 🧠",
      emoji: "🧠",
      elements: [{ type: "dashboard" as const }],
    },
    {
      id: 6,
      title: "Your Casino is Live! 🚀",
      emoji: "🚀",
      elements: [{ type: "launch" as const }],
    },
  ],
  colors: {
    background: "transparent",
    backgroundGradient: "transparent",
    primary: "rgb(var(--color1))",
    secondary: "rgb(var(--color2))",
    accent: "rgb(var(--color2))",
    textGlow: "rgba(var(--color1), 0.6)",
  },
  timing: {
    stepDuration: 4000,
    transitionDuration: 800,
  },
  text: {
    titleSize: "4rem",
    stepSize: "2rem",
  },
};

const normalizePricingPlans = (plans?: any[]): PricingPlan[] => {
  if (!Array.isArray(plans) || plans.length === 0) {
    return defaultPricingContent.plans;
  }

  return plans.map((plan, index) => {
    const fallbackId =
      typeof plan?.name === "string"
        ? plan.name.toLowerCase().replace(/\s+/g, "-")
        : `plan-${index + 1}`;

    return {
      id: plan?.id || fallbackId,
      name: plan?.name || `Plan ${index + 1}`,
      badge: plan?.badge,
      description: plan?.description || "",
      priceDisplay:
        plan?.priceDisplay ||
        plan?.setupFee?.amount ||
        plan?.subscription?.amount ||
        defaultPricingContent.plans[index % defaultPricingContent.plans.length].priceDisplay,
      currency: plan?.currency || "USD",
      hasThemeStudio: plan?.hasThemeStudio ?? false,
      features: Array.isArray(plan?.features)
        ? plan.features
        : defaultPricingContent.plans[index % defaultPricingContent.plans.length].features || [],
      button: plan?.button || "CHOOSE A PLAN",
      setupFee: plan?.setupFee || { label: "", amount: "", period: "" },
      subscription: plan?.subscription || { label: "", amount: "", note: "" },
    } as PricingPlan;
  });
};

function AppContent() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [themeKey, setThemeKey] = useState(0);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "register">("login");
  const [heroContent, setHeroContent] = useState<HeroContent | null>(null);
  const [howItWorksConfig, setHowItWorksConfig] = useState<HowItWorksConfig>(defaultConfig);
  const [pickYourFocusConfig, setPickYourFocusConfig] = useState<any>(null);
  const [featuresConfig, setFeaturesConfig] = useState<FeaturesConfig | null>(null);
  const [audienceCardsConfig, setAudienceCardsConfig] = useState<AudienceCardsConfig | null>(null);
  const [keyDifferencesConfig, setKeyDifferencesConfig] = useState<{ title: string; features: FeatureItem[] } | null>(null);
  const [pricingConfig, setPricingConfig] = useState<PricingContent>(defaultPricingContent);
  const [footerConfig, setFooterConfig] = useState<FooterConfig | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<string>("maincard");
  const [headerLogoConfig, setHeaderLogoConfig] = useState<HeaderLogoConfig | null>(null);
  const [faqConfig, setFaqConfig] = useState<FAQConfig | null>(null);
  const [demoFormOpen, setDemoFormOpen] = useState(false);
  const [isOrdersDialogOpen, setIsOrdersDialogOpen] = useState(false);
  const [isContentLoading, setIsContentLoading] = useState(true);

  const { user, isLoading } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { openDialog: openBusinessDialog } = useBusinessContext();

  const pageStyle = {
    background: "transparent",
    minHeight: "auto",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    gap: "60px",
  };

  // Reset auth mode when user logs out
  useEffect(() => {
    if (!user) {
      setAuthMode("login");
      setIsOrdersDialogOpen(false);
    }
  }, [user]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const handleOpenOrders = () => setIsOrdersDialogOpen(true);
    window.addEventListener(OPEN_ORDERS_DIALOG_EVENT, handleOpenOrders);
    return () => window.removeEventListener(OPEN_ORDERS_DIALOG_EVENT, handleOpenOrders);
  }, []);

  useEffect(() => {
    // Apply initial theme
    console.log("🎨 App: Applying initial theme");
    applyThemeToDocument();

    // Log initial CSS variables
    const root = document.documentElement;
    console.log("🎨 App: Initial CSS variables:");
    console.log(
      "  --color-bg1:",
      getComputedStyle(root).getPropertyValue("--color-bg1")
    );
    console.log(
      "  --color1:",
      getComputedStyle(root).getPropertyValue("--color1")
    );
    console.log(
      "  --color2:",
      getComputedStyle(root).getPropertyValue("--color2")
    );

    // Handle scroll to create smooth transition
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;

      // Calculate scroll progress (0 to 1)
      // Start fading at 30% scroll, fully faded at 80% scroll to keep globes visible in hero
      const fadeStart = windowHeight * 0.3;
      const fadeEnd = windowHeight * 0.8;

      let progress = 0;
      if (scrollY > fadeStart) {
        progress = Math.min((scrollY - fadeStart) / (fadeEnd - fadeStart), 1);
        // Use easing function for smoother transition
        progress = progress * progress * (3 - 2 * progress); // Smooth step function
      }

      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const loadContent = async () => {
      const startTime = Date.now();
      setIsContentLoading(true);
      try {
        // Fetch from API - fallback to JSON file if API fails
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8080";
        const response = await fetch(`${apiUrl}/api/v1/landing-page/content`);

        if (!response.ok) {
          // Fallback to JSON file if API is not available
          console.warn("API not available, falling back to JSON file");
          const jsonResponse = await fetch("/hero-content.json");
          if (jsonResponse.ok) {
            const data: HeroContent = await jsonResponse.json();
            setHeroContent(data);
          }
          // Use default config for how it works
          setHowItWorksConfig(defaultConfig);
          return;
        }

        const apiData = await response.json();

        // Extract hero section from the API response
        if (apiData.hero) {
          setHeroContent(apiData.hero as HeroContent);
        } else {
          // If hero section doesn't exist, try to use the JSON file
          const jsonResponse = await fetch("/hero-content.json");
          if (jsonResponse.ok) {
            const data: HeroContent = await jsonResponse.json();
            setHeroContent(data);
          }
        }

        // Extract how-it-works section from the API response
        if (apiData["how-it-works"]) {
          const hiwData = apiData["how-it-works"] as HowItWorksConfig;
          // Ensure steps have proper typing
          const processedConfig: HowItWorksConfig = {
            ...hiwData,
            steps: hiwData.steps.map((step) => ({
              ...step,
              elements: step.elements.map((el) => ({
                type: el.type as "globe" | "cards" | "input" | "crypto" | "dashboard" | "launch",
                content: el.content,
                packages: el.packages || undefined,
              })),
            })),
          };
          setHowItWorksConfig(processedConfig);
        } else {
          // Use default config if not found
          setHowItWorksConfig(defaultConfig);
        }

        // Extract pick-your-focus section from the API response
        if (apiData["pick-your-focus"]) {
          setPickYourFocusConfig(apiData["pick-your-focus"]);
        }

        // Extract features section from the API response
        if (apiData["features"]) {
          setFeaturesConfig(apiData["features"] as FeaturesConfig);
        } else {
          // Use default features if not found
          setFeaturesConfig({
            items: [
              {
                id: "configure-payments",
                title: "Configure Payments",
                description: "Integrate 300+ fiat and crypto methods in just a few clicks.",
                imageUrl: "",
              },
              {
                id: "design-your-brand",
                title: "Design Your Brand",
                description: "Use our drag-and-drop builder to create your unique UI. No coding required.",
                imageUrl: "",
              },
              {
                id: "launch-globally",
                title: "Launch Globally",
                description: "Go live in minutes with licensing coverage across 185 countries.",
                imageUrl: "",
              },
            ],
          });
        }

        // Extract audience-cards section from the API response
        if (apiData["audience-cards"]) {
          setAudienceCardsConfig(apiData["audience-cards"] as AudienceCardsConfig);
        } else {
          // Use default cards if not found
          setAudienceCardsConfig({
            cards: [
              {
                id: "casino-operators",
                heading: "For Casino Operators",
                subheading: "Scale into New Markets—instantly",
                checklistItems: [
                  "Expand into new GEOs 1000x faster—no dev team needed",
                  "Change design post-launch with zero downtime",
                  "Switch from outdated providers and go crypto-friendly",
                  "Full-stack solution: payments, KYC/AML, risk, CRM, affiliates",
                  "All day-to-day operations handled for you",
                ],
                ctaText: "SCALE FASTER WITH MAINCARD",
              },
              {
                id: "affiliate-managers",
                heading: "For Affiliate Managers",
                subheading: "Own the Brand. Keep the Profits",
                checklistItems: [
                  "Stop sending traffic to others—build your own brand",
                  "4x profits with the same traffic",
                  "Boost LTV with built-in retention tools",
                  "Integrated affiliate tracking & player analytics",
                ],
                ctaText: "TURN TRAFFIC INTO REVENUE",
              },
              {
                id: "influencers",
                heading: "For Influencers",
                subheading: "Monetize your fan base",
                checklistItems: [
                  "Promote your own brand instead of advertising others",
                  "Monetize your audience through interactive experience",
                  "Use your branded token as in-game currency",
                  "Engage fans with real-time casino overlays & live events",
                  "0 efforts to manage day-to-day operations",
                ],
                ctaText: "MONETIZE YOUR INFLUENCE",
              },
              {
                id: "first-time-founders",
                heading: "For the first-time founders",
                subheading: "Start Your iGaming Business with as little as $4,999",
                checklistItems: [
                  "Instant launch, full support, multiple channels for growth",
                  "No development needed, focus on generating traffic",
                  "100% coverage from losses",
                  "$0 in maintenance fees",
                ],
                ctaText: "START YOUR FIRST BUSINESS WITH US",
              },
            ],
          });
        }

        // Extract key-differences section from the API response
        if (apiData["key-differences"]) {
          setKeyDifferencesConfig(apiData["key-differences"] as { title: string; features: FeatureItem[] });
        } else {
          // Use default key differences if not found
          setKeyDifferencesConfig({
            title: "Key Differences",
            features: [
              {
                text: "10'000x faster than any other provider in the market",
              },
              {
                text: "Unlimited design options. Even after launch",
              },
              {
                text: "15'000+ games and unlimited sports at 0 monthly cost",
              },
              {
                text: "The most flexible pricing for setup",
              },
              {
                text: "100% loss coverage",
              },
              {
                text: "Licensing across 184 countries and the US (for Sweepstakes)",
              },
              {
                text: "Full coverage of day-to-day operations, including:",
                subItems: [
                  "Players' KYC, Anti-fraud, anti-bonus abuse",
                  "Fiat & Crypto payments, over 300 payment methods supported",
                  "Built-in CRM and Affiliate platform operated by us",
                  "24/7 customer support, including VIP players",
                ],
              },
            ],
          });
        }

        // Extract pricing section from the API response
        if (apiData["pricing"]) {
          const pricingData = apiData["pricing"] as Partial<PricingContent>;
          setPricingConfig({
            title: pricingData.title || defaultPricingContent.title,
            subtitle: pricingData.subtitle || defaultPricingContent.subtitle,
            plans: normalizePricingPlans(pricingData.plans),
          });
        } else {
          // Use default pricing if not found
          setPricingConfig(defaultPricingContent);
        }

        // Extract footer section from the API response
        if (apiData["footer"]) {
          setFooterConfig(apiData["footer"] as FooterConfig);
        } else {
          // Use default footer if not found
          setFooterConfig({
            newsletter: {
              title: "Build Your Casino Empire",
              description: "Get the latest casino builder updates, industry insights, and exclusive offers. Launch your iGaming platform in 30 minutes.",
              emailPlaceholder: "your@email.com",
              buttonText: "Get Started",
            },
            navigation: [
              { label: "How It Works", href: "#how-it-works" },
              { label: "Pricing", href: "#pricing" },
              { label: "Support", href: "#support" },
              { label: "Licensing", href: "#licensing" },
              { label: "Privacy", href: "#privacy" },
            ],
            social: [
              { name: "LinkedIn", href: "#", icon: "linkedin" },
              { name: "Twitter/X", href: "#", icon: "twitter" },
              { name: "Telegram", href: "#", icon: "telegram" },
            ],
            copyright: "© 2025 CSZGLOBAL. All rights reserved.",
          });
        }

        // Extract theme-settings section from the API response
        if (apiData["theme-settings"]) {
          const themeData = apiData["theme-settings"] as { selectedTheme: string };
          if (themeData.selectedTheme && colorThemes[themeData.selectedTheme]) {
            setSelectedTheme(themeData.selectedTheme);
            setCurrentTheme(themeData.selectedTheme);
            applyThemeToDocument();
          }
        } else {
          // Use default theme if not found
          setSelectedTheme("maincard");
          setCurrentTheme("maincard");
          applyThemeToDocument();
        }

        // Extract header-logo section from the API response
        if (apiData["header-logo"]) {
          setHeaderLogoConfig(apiData["header-logo"] as HeaderLogoConfig);
        } else {
          // Fallback to default header logo config
          setHeaderLogoConfig({
            showLogo: false,
            showBrandName: true,
            order: "brand-first",
            brandName: "CSZGLOBAL",
            brandNameFontSize: "1.5rem",
            logoImageSize: "40px",
            logoImageUrl: "",
          });
        }

        // Extract FAQ section from the API response
        if (apiData["faq"]) {
          setFaqConfig(apiData["faq"] as FAQConfig);
        } else {
          // Fallback to default FAQ config
          setFaqConfig({
            title: "Frequently Asked Questions",
            subtitle: "Everything you need to know about our platform",
            items: [],
          });
        }
      } catch (error) {
        console.error("Error loading content", error);
        // Final fallback to JSON file and default config
        try {
          const jsonResponse = await fetch("/hero-content.json");
          if (jsonResponse.ok) {
            const data: HeroContent = await jsonResponse.json();
            setHeroContent(data);
          }
        } catch (fallbackError) {
          console.error("Failed to load fallback JSON", fallbackError);
        }
        setHowItWorksConfig(defaultConfig);
        // Apply default theme on error
        setSelectedTheme("maincard");
        setCurrentTheme("maincard");
        applyThemeToDocument();
      } finally {
        // Ensure loader shows for minimum 3 seconds
        const elapsedTime = Date.now() - startTime;
        const minDisplayTime = 5000; // 3 seconds in milliseconds
        const remainingTime = Math.max(0, minDisplayTime - elapsedTime);

        setTimeout(() => {
          setIsContentLoading(false);
        }, remainingTime);
      }
    };

    loadContent();
  }, []);

  // Apply theme when selectedTheme changes
  useEffect(() => {
    if (selectedTheme && colorThemes[selectedTheme]) {
      setCurrentTheme(selectedTheme);
      applyThemeToDocument();
    }
  }, [selectedTheme]);

  const handleThemeChange = (themeName: string) => {
    // Force re-render to update the animated background
    setThemeKey((prev) => prev + 1);
  };

  const handleAuthSuccess = () => {
    showSuccess(
      `Welcome${user ? ` back, ${getUserFullName(user)}` : ""
      }! You're now signed in to CSZGLOBAL Casino Builder.`
    );
  };

  const handleSignInClick = () => {
    setAuthMode("login");
    setAuthDialogOpen(true);
  };

  const handleSignUpClick = () => {
    setAuthMode("register");
    setAuthDialogOpen(true);
  };

  // Reset auth mode when dialog closes
  const handleAuthDialogClose = () => {
    setAuthDialogOpen(false);
    setAuthMode("login"); // Always reset to login mode
  };

  const handleProfileClick = () => {
    setProfileOpen(true);
  };

  const handleOrdersClick = () => {
    if (user) {
      setIsOrdersDialogOpen(true);
    } else {
      handleSignInClick();
    }
  };

  return (
    <PricingProvider value={pricingConfig}>
      <div className="App">
        {/* Animated background with smooth opacity transition */}
        <AnimatedBackground key={themeKey} scrollProgress={scrollProgress} />

        {/* Smooth transition overlay */}
        <div
          className="transition-overlay"
          style={{
            opacity: Math.max(0, scrollProgress - 0.2),
            background: `linear-gradient(to bottom, 
            transparent 0%, 
            rgba(0, 0, 0, ${Math.max(0, scrollProgress - 0.2) * 0.3}) 50%, 
            rgba(0, 0, 0, ${Math.max(0, scrollProgress - 0.2) * 0.5}) 100%
          )`,
          }}
        ></div>

        {/* Navigation with auth support */}
        <Navbar
          onSignInClick={handleSignInClick}
          onSignUpClick={handleSignUpClick}
          onProfileClick={handleProfileClick}
          onOrdersClick={handleOrdersClick}
          user={user}
          headerLogoConfig={headerLogoConfig || undefined}
        />

        {/* Theme Switcher - Commented out, theme is now managed from admin panel */}
        {/* <ThemeSwitcher onThemeChange={handleThemeChange} /> */}

        {/* Main content sections */}
        <main>
          <section id="hero" className="hero-section">
            <div className="hero-content">
              <h1>
                {heroContent?.title ?? "Launch your iGaming brand in 30 minutes"}
              </h1>
              <p className="tagline">
                {heroContent?.tagline ??
                  "No Code. No maintenance fees. No Limits."}
              </p>
              <p className="description">
                {heroContent?.description ??
                  "Create a licensed, fully operational casino, online lottery, or sportsbook — no developers, no tech team, no support team needed."}
              </p>
              <div className="hero-cta">
                <button
                  className="cta-button primary"
                  onClick={
                    user
                      ? () => openBusinessDialog()
                      : handleSignUpClick
                  }
                >
                  {heroContent?.primaryCtaLabel ?? "START BUILDING"}
                </button>
                <button
                  className="cta-button secondary"
                  onClick={() => setDemoFormOpen(true)}
                >
                  {heroContent?.secondaryCtaLabel ?? "BOOK A DEMO"}
                </button>
              </div>
            </div>
          </section>

          <HowItWorksPlayer config={howItWorksConfig} pricingPlans={pricingConfig?.plans} />

          <PickYourFocus config={pickYourFocusConfig ? { content: pickYourFocusConfig } : undefined} />

          <Features config={featuresConfig || undefined} />

          <AudienceCards config={audienceCardsConfig || undefined} />

          {keyDifferencesConfig && (
            <KeyDifferences
              title={keyDifferencesConfig.title}
              features={keyDifferencesConfig.features}
            />
          )}

          {/* <section id="key-differences" className="section dark-section">
          <div className="section-content">
            <h2 className="section-title">Key Differences</h2>
            <div className="features-list">
              <div className="feature-item">
                <div className="feature-icon">✓</div>
                <span>10'000x faster than any other provider in the market</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">✓</div>
                <span>Unlimited design options. Even after launch</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">✓</div>
                <span>15'000+ games and unlimited sports at 0 monthly cost</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">✓</div>
                <span>The most flexible pricing for setup</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">✓</div>
                <span>100% loss coverage</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">✓</div>
                <span>Licensing across 184 countries and the US (for Sweepstakes)</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">✓</div>
                <span>Full coverage of day-to-day operations, including:</span>
              </div>
              <div className="sub-features">
                <div className="feature-item">
                  <div className="feature-icon">✓</div>
                  <span>Players' KYC, Anti-fraud, anti-bonus abuse</span>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">✓</div>
                  <span>Fiat & Crypto payments, over 300 payment methods supported</span>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">✓</div>
                  <span>Built-in CRM and Affiliate platform operated by us</span>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">✓</div>
                  <span>24/7 customer support, including VIP players</span>
                </div>
              </div>
            </div>
          </div>
        </section> */}

          {/* <section id="pricing" className="section dark-section">
          <div className="section-content">
            <h2 className="section-title">Pricing</h2>
            <p className="pricing-tagline">
              You pick the approach based on your confidence. We help you grow.
            </p>
            <div className="pricing-cards">
              <div className="pricing-card">
                <h3 className="plan-title">The Cautious Creator</h3>
                <div className="pricing-details">
                  <p className="setup-label">One-time setup fee:</p>
                  <p className="setup-price">$4'999</p>
                  <p className="monthly-sub">Monthly subscription: $799</p>
                  <p className="revenue-share">
                    Revenue-sharing: 15% of the GGR
                  </p>
                </div>
                <button className="choose-plan-btn">CHOOSE A PLAN</button>
          </div>
              
              <div className="pricing-card">
                <h3 className="plan-title">The All-in Pro</h3>
                <div className="pricing-details">
                  <p className="setup-label">One-time setup fee:</p>
                  <p className="setup-price">$19'999</p>
                  <p className="monthly-sub">Monthly subscription: None</p>
                  <p className="revenue-share">
                    Revenue-sharing: 15% of the GGR
                  </p>
                </div>
                <button className="choose-plan-btn">CHOOSE A PLAN</button>
              </div>
            </div>
          </div>
        </section> */}

          <PricingSection
            onClick={
              user
                ? () => openBusinessDialog()
                : handleSignUpClick
            }
            config={{
              content: {
                title: pricingConfig.title,
                subtitle: pricingConfig.subtitle,
                plans: pricingConfig.plans.map((plan) => ({
                  ...plan,
                  button: plan.button || "CHOOSE A PLAN",
                  setupFee: {
                    label: plan.setupFee?.label || "",
                    amount: plan.setupFee?.amount || "",
                    period: plan.setupFee?.period || "",
                  },
                  subscription: {
                    label: plan.subscription?.label || "",
                    amount: plan.subscription?.amount || "",
                    note: plan.subscription?.note || "",
                  },
                })),
              },
            }}
          />

          {/* FAQ Section */}
          <FAQ config={faqConfig || undefined} />
        </main>

        {/* Footer */}
        <Footer config={footerConfig || undefined} />

        {/* Authentication Dialog */}
        <AuthDialog
          isOpen={authDialogOpen}
          onClose={handleAuthDialogClose}
          initialMode={authMode}
          onAuthSuccess={handleAuthSuccess}
        />

        {/* Profile Dialog */}
        <Profile isOpen={profileOpen} onClose={() => setProfileOpen(false)} />

        {/* Global Loader */}
        {isLoading && <Loader message="Processing..." />}

        {/* Content Loading Loader */}
        {isContentLoading && <Loader message="Loading your casino experience..." size="large" />}

        {/* Demo Request Form */}
        <DemoRequestForm
          isOpen={demoFormOpen}
          onClose={() => setDemoFormOpen(false)}
          onSubmitSuccess={() => {
            showSuccess("Demo request submitted successfully! We'll contact you soon.");
          }}
        />

        {/* Business Flow Dialogs */}
        <CountrySelectionDialog />
        <CountryDetailsDialog />
        <PricingPlansDialog />
        <BusinessSetupDialog />
        <WebsiteStudioDialog />
        <OrderDetailsDialog />
        <PaymentDialog />
        <UserOrdersSection
          isOpen={isOrdersDialogOpen}
          onClose={() => setIsOrdersDialogOpen(false)}
        />
      </div>
    </PricingProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <BusinessProvider>
        <NotificationProvider>
          <AppContent />
        </NotificationProvider>
      </BusinessProvider>
    </AuthProvider>
  );
}

export default App;
